import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { addSearchParams, stripSearchString } from 'shared/utils';
import { useNotification, useRequest } from 'shared/hooks';
import { FILTER_DISPLAY_TYPE } from '../../constants';

// Make a stable default value.
const emptyFilters = [];

const initialFilterState = {
  limit: 0,
  order: '',
  orderBy: '',
  page: 1,
  query: '',
};

const initialTableState = {
  availablePages: 0,
  filterState: initialFilterState,
  rows: undefined, // Make distinction between rows haven't been loaded and 0 search results.
  total: 0,
};

export function flattenFilters(filters) {
  return filters?.reduce((result, filter) => {
    if (filter.type === FILTER_DISPLAY_TYPE.control) {
      return [...result, filter];
    }
    if (filter.type === FILTER_DISPLAY_TYPE.group) {
      return [...result, ...filter.items];
    }
    return result;
  }, []);
}

// Provides state and callback functions for a paginated, orderable, filterable table.
// URL search parameters are the source truth. This allows for easy linking to specific table views.
// This is the basic flow:
//
// 1: Component mounts and it loads data from the current search param.
// 2: User changes the search params.
// 3: react-router passes down the new search prop which causes a data reload.
export default function useDataTable(options) {
  const {
    baseSearch,
    defaultLimit = 500, // Limit must be set for pagination to work.
    filters = emptyFilters,
    ignoredKeys,
    searchRequest,
    showDefaultOptions = true, // Whether to do a query when no filters are applied.
    updateCount,
  } = options;

  const { push: pushHistory } = useHistory();
  const { search } = useLocation();
  const { request, isLoading } = useRequest();
  const [state, setState] = useState(initialTableState);
  const notification = useNotification();

  const {
    availablePages,
    defaultSearch,
    filterState: {
      order,
      orderBy,
      page,
    },
  } = state;

  // Important to calculate this outside of the effect so that the dependency array is stable.
  const strippedSearch = stripSearchString(search, ignoredKeys).toString();

  // Sets the state to match the params in the url.
  // Possible actions that trigger this:
  //
  // 1. history.push is called
  // 2. User presses the browsers back/forward button
  // 3. User presses a link to this table.
  // 4. Any of the props in the dependency list change.
  useEffect(() => {
    const controlledSearchParams = new URLSearchParams(strippedSearch);

    // After reading expected params remove them.
    // This lets us know the extra parameters that this table doesn't control.
    const popParam = (param) => {
      const value = controlledSearchParams.getAll(param);
      controlledSearchParams.delete(param);
      if (value.length === 1) {
        return value[0];
      }
      if (value.length > 1) {
        return value;
      }
      return '';
    };

    const flatFilters = flattenFilters(filters);

    const newFilterState = flatFilters.reduce((result, { param, defaultValue }) => {
      const current = { ...result };

      current[param] = popParam(param, defaultValue);
      return current;
    }, {
      limit: parseInt(popParam('l'), 10) || defaultLimit,
      // 'asc' or 'desc'.
      order: popParam('o') || '',
      // A key from the columns prop.
      orderBy: popParam('ob') || '',
      page: parseInt(popParam('p'), 10) || 1,
      query: popParam('q') || '',
    });

    (async () => {
      const searchParams = addSearchParams(strippedSearch, baseSearch);

      if (!showDefaultOptions && searchParams.toString().length <= 2) {
        setState(initialTableState);
        return;
      }

      // Make sure a limit is set.
      if (!searchParams.get('l')) {
        searchParams.set('l', defaultLimit);
      }

      // Set new filters immediately.
      setState((current) => ({
        ...current,
        filterState: newFilterState,
      }));

      const result = await request(searchRequest(`?${searchParams.toString()}`, 'application/json'));
      if (!result) return;
      setState((current) => ({
        ...current,
        availablePages: Math.ceil(result.total / newFilterState.limit),
        dataTitle: result.title, // Optionally allow backend to set table title.
        defaultSearch: `?${controlledSearchParams.toString()}`, // Search string to set if user resets the table.
        rows: result.rows || [],
        total: result.total,
      }));
    })();
  }, [
    baseSearch,
    defaultLimit,
    filters,
    request,
    searchRequest,
    showDefaultOptions,
    strippedSearch,
    updateCount,
  ]);

  // Update the url to match filter state and trigger a table reload.
  const setFilters = useCallback((kvPairs) => {
    const searchParams = new URLSearchParams(search);
    kvPairs.forEach((pair) => {
      const key = pair[0];
      const value = pair[1];

      // Update the URL to reflect the new filter.
      if (value) {
        if (Array.isArray(value)) {
          searchParams.delete(key);
          value.forEach((val) => {
            // only append to the search params if the key value pair does not already exist
            searchParams.append(key, val);
          });
        } else {
          searchParams.set(key, value);
        }
      } else searchParams.delete(key);
    });

    // Triggers the load data effect.
    pushHistory(`?${searchParams.toString()}`);
  }, [search, pushHistory]);

  const removeFilterChip = useCallback((kvPair, dependentSubKeys) => {
    const searchParams = new URLSearchParams(search);
    const newSearchParams = new URLSearchParams();
    const chipKey = kvPair[0];
    const chipValue = kvPair[1];

    searchParams.forEach((value, key) => {
      // if key and value exist in search param, do not add to search params
      if (!(value === chipValue && key === chipKey) && !dependentSubKeys.includes(key)) {
        newSearchParams.append(key, value);
      }
    });

    // Triggers the load data effect.
    pushHistory(`?${newSearchParams.toString()}`);
  }, [search, pushHistory]);

  // When order is not set, order descending.
  // When order is set, and a different column's order is toggled, order descending
  // When order is set to desc, and the same column's order is toggled, order ascending.
  //
  // This is a callback because it's passed to the memoized DynamicTable component.
  const onReorder = useCallback((columnKey) => {
    if (orderBy !== columnKey) {
      setFilters([['ob', columnKey], ['o', 'desc']]);
    } else if (order === 'asc') {
      setFilters([['ob', ''], ['o', '']]);
    } else if (order === 'desc') {
      setFilters([['ob', columnKey], ['o', 'asc']]);
    }
  }, [orderBy, order, setFilters]);

  const pageFirst = () => {
    setFilters([['p', 0]]);
  };

  const pageForward = () => {
    const newPage = page + 1;

    if (newPage <= availablePages) {
      setFilters([['p', newPage]]);
    }
  };

  const pageBack = () => {
    const newPage = page - 1;
    if (newPage <= 0) return;

    if (newPage === 1) {
      setFilters([['p', '']]);
    } else {
      setFilters([['p', newPage]]);
    }
  };

  const pageLast = () => {
    setFilters([['p', availablePages]]);
  };

  const setLimit = (newLimit) => {
    if (newLimit === defaultLimit) {
      setFilters([['l', ''], ['p', '']]);
    } else {
      setFilters([['l', newLimit], ['p', '']]);
    }
  };

  const exportCSV = () => {
    const searchParams = addSearchParams(strippedSearch, baseSearch);

    // Export the entire result set.
    searchParams.delete('l');
    searchParams.delete('p');
    request(searchRequest(`?${searchParams.toString()}`, 'text/csv'), 'Download complete');
  };

  const clearTable = () => {
    if (defaultSearch) {
      pushHistory(defaultSearch.replace(baseSearch, ''));
    } else {
      pushHistory('?');
    }
    notification.pushInfo('Filters Cleared');
  };

  return {
    clearTable,
    exportCSV,
    isLoading,
    onReorder,
    pageBack,
    pageFirst,
    pageForward,
    pageLast,
    search,
    setFilters,
    removeFilterChip,
    setLimit,
    tableState: state,
  };
}
